import {makeAutoObservable, runInAction} from "mobx";
import {RootStore} from "./RootStore";

export type Breakpoint = 'xs' | 'sm' | 'md' | 'lg' | 'xl';
export type Orientation = 'portrait' | 'landscape';
export type PointerType = 'fine' | 'coarse' | 'none';

// MUI default breakpoint values (px). Must stay in sync with the theme in PrefStore.
const BREAKPOINT_VALUES: Record<Breakpoint, number> = {
  xs: 0,
  sm: 600,
  md: 900,
  lg: 1200,
  xl: 1536,
};

function widthToBreakpoint(width: number): Breakpoint {
  if (width >= BREAKPOINT_VALUES.xl) return 'xl';
  if (width >= BREAKPOINT_VALUES.lg) return 'lg';
  if (width >= BREAKPOINT_VALUES.md) return 'md';
  if (width >= BREAKPOINT_VALUES.sm) return 'sm';
  return 'xs';
}

type MediaQueryEntry = {
  mql: MediaQueryList;
  listener: (e: MediaQueryListEvent) => void;
};

export class UiStore {
  // UiStore es self-contained: detecta viewport/device via window APIs y no
  // necesita acceso al rootStore. El constructor recibe rootStore para mantener
  // la convención del proyecto (otros stores lo necesitan), pero no se guarda.

  width: number = 0;
  height: number = 0;
  breakpoint: Breakpoint = 'lg';

  orientation: Orientation = 'landscape';
  pointerType: PointerType = 'fine';
  isTouchDevice: boolean = false;
  isStandalonePWA: boolean = false;
  devicePixelRatio: number = 1;

  prefersReducedMotion: boolean = false;
  prefersDarkMode: boolean = false;
  prefersHighContrast: boolean = false;

  private resizeRafId: number | null = null;
  private windowListeners: Array<{event: string, handler: EventListener}> = [];
  private mediaQueries: MediaQueryEntry[] = [];

  constructor(_rootStore: RootStore) {
    this.snapshot();
    this.setupListeners();
    makeAutoObservable(this);
  }

  get isMobile(): boolean {
    return this.breakpoint === 'xs' || this.breakpoint === 'sm';
  }

  get isTablet(): boolean {
    return this.breakpoint === 'md';
  }

  get isDesktop(): boolean {
    return this.breakpoint === 'lg' || this.breakpoint === 'xl';
  }

  private snapshot() {
    if (typeof window === 'undefined') return;

    this.width = window.innerWidth;
    this.height = window.innerHeight;
    this.breakpoint = widthToBreakpoint(this.width);
    this.devicePixelRatio = window.devicePixelRatio || 1;
    this.isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    this.orientation = this.width >= this.height ? 'landscape' : 'portrait';

    this.pointerType = this.readMatchMedia('(pointer: coarse)') ? 'coarse'
      : this.readMatchMedia('(pointer: fine)') ? 'fine'
      : 'none';
    this.isStandalonePWA = this.readMatchMedia('(display-mode: standalone)')
      || (window.navigator as any).standalone === true;
    this.prefersReducedMotion = this.readMatchMedia('(prefers-reduced-motion: reduce)');
    this.prefersDarkMode = this.readMatchMedia('(prefers-color-scheme: dark)');
    this.prefersHighContrast = this.readMatchMedia('(prefers-contrast: more)');
  }

  private readMatchMedia(query: string): boolean {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    try {
      return window.matchMedia(query).matches;
    } catch {
      return false;
    }
  }

  private setupListeners() {
    if (typeof window === 'undefined') return;

    const handleResize = () => {
      if (this.resizeRafId !== null) {
        cancelAnimationFrame(this.resizeRafId);
      }
      this.resizeRafId = requestAnimationFrame(() => {
        this.resizeRafId = null;
        runInAction(() => {
          this.width = window.innerWidth;
          this.height = window.innerHeight;
          this.breakpoint = widthToBreakpoint(this.width);
          this.orientation = this.width >= this.height ? 'landscape' : 'portrait';
        });
      });
    };
    window.addEventListener('resize', handleResize);
    this.windowListeners.push({event: 'resize', handler: handleResize as EventListener});

    const handleOrientation = () => {
      runInAction(() => {
        this.orientation = window.innerWidth >= window.innerHeight ? 'landscape' : 'portrait';
      });
    };
    window.addEventListener('orientationchange', handleOrientation);
    this.windowListeners.push({event: 'orientationchange', handler: handleOrientation as EventListener});

    this.watchMediaQuery('(pointer: coarse)', (matches) => {
      this.pointerType = matches ? 'coarse'
        : this.readMatchMedia('(pointer: fine)') ? 'fine'
        : 'none';
    });
    this.watchMediaQuery('(display-mode: standalone)', (matches) => {
      this.isStandalonePWA = matches || (window.navigator as any).standalone === true;
    });
    this.watchMediaQuery('(prefers-reduced-motion: reduce)', (matches) => {
      this.prefersReducedMotion = matches;
    });
    this.watchMediaQuery('(prefers-color-scheme: dark)', (matches) => {
      this.prefersDarkMode = matches;
    });
    this.watchMediaQuery('(prefers-contrast: more)', (matches) => {
      this.prefersHighContrast = matches;
    });
  }

  private watchMediaQuery(query: string, onChange: (matches: boolean) => void) {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    let mql: MediaQueryList;
    try {
      mql = window.matchMedia(query);
    } catch {
      return;
    }
    const listener = (e: MediaQueryListEvent) => {
      runInAction(() => onChange(e.matches));
    };
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', listener);
    } else if (typeof (mql as any).addListener === 'function') {
      (mql as any).addListener(listener);
    }
    this.mediaQueries.push({mql, listener});
  }

  destroy() {
    if (this.resizeRafId !== null) {
      cancelAnimationFrame(this.resizeRafId);
      this.resizeRafId = null;
    }
    for (const {event, handler} of this.windowListeners) {
      window.removeEventListener(event, handler);
    }
    this.windowListeners = [];
    for (const {mql, listener} of this.mediaQueries) {
      if (typeof mql.removeEventListener === 'function') {
        mql.removeEventListener('change', listener);
      } else if (typeof (mql as any).removeListener === 'function') {
        (mql as any).removeListener(listener);
      }
    }
    this.mediaQueries = [];
  }
}
